import { Condition, TextConditionVariant, TextConditionVariants } from 'editor/src/store/design/types';

/**
 * The content one option contributes to a text that varies with a Condition —
 * the single place every reader of `TextConditionVariants` goes through, so
 * they all agree on what an option currently holds.
 *
 * Two shapes produce that content. The default (see `TextConditionVariants`)
 * is the author's own per-option string in `values`, and an unauthored option
 * resolves to undefined so the caller can leave the element's text untouched —
 * this is the whole of today's behaviour, unchanged for any existing design.
 * `useOptionLabel` replaces that with the option's own label and ignores
 * `values` entirely, which is what makes an hours picklist ("1:00 AM"…)
 * require no authoring at all: the printed text is just whichever option the
 * buyer picked. No font travels with a label — the element keeps whatever it
 * already renders in.
 *
 * Undefined either way when `optionId` names an option the condition no
 * longer has (a stale reference, or a caller mid-way through an option
 * change) — and, in label mode, when `condition` itself is unavailable. The
 * per-option path never needed the condition at all, so `condition` is
 * optional rather than required: a caller that has not looked it up (or
 * could not — an orphaned conditionId) still gets today's exact per-option
 * behaviour, and only loses label-mode resolution, which has nothing to
 * resolve without it anyway.
 */
const resolveTextVariant = (
  variants: TextConditionVariants,
  condition: Condition | undefined,
  optionId: string,
): TextConditionVariant | undefined => {
  if (variants.useOptionLabel) {
    const option = condition?.options.find((candidate) => candidate.id === optionId);
    return option ? { text: option.label } : undefined;
  }
  return variants.values[optionId];
};

export default resolveTextVariant;
